import {
  Box,
  Button,
  Center,
  Flex,
  Grid,
  GridItem,
  Icon,
  Table,
  Tag,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { debounce } from "lodash";
import { useCallback, useEffect, useState } from "react";
import { ListPageHeader, listHeaderRow } from "../../../common/components/ListPageHeader";
import { useTranslation } from "react-i18next";
import { FaUndo } from "react-icons/fa";
import { useLocation, useNavigate } from "react-router-dom";
import { ProjectStatus, useGetProjectsQuery } from "../../../autogen/bff-api";
import { useDisplayDate } from "../../../common/formatting/useDisplayDate";
import type { EntityTableColumn } from "../../../common/entity-table/EntityTable";
import { FilterBar } from "../../../common/entity-table/FilterBar";
import { ListLoadError } from "../../../common/ListLoadError";
import { Paging } from "../../../common/Paging";
import { useSub } from "../../../common/subscription/useSub";
import { useSkeletons } from "../../../common/useSkeletons";
import { displayPersonName } from "../../../common/persons/displayPersonName";
import { useProjectNavigation } from "../useProjectNavigation";
import { knownProjectStatuses, useProjectStatuses } from "../../../common/projects/useProjectStatuses";
import { useProjectTypes } from "../useProjectTypes";

export const Projects = () => {
  const { t } = useTranslation();
  const displayDate = useDisplayDate();
  const navigate = useNavigate();
  const location = useLocation();
  const sub = useSub();
  const goToProject = useProjectNavigation();
  const noAccess = !sub.hasProjectAccess;

  const skeletons = useSkeletons();
  const projectTypes = useProjectTypes();
  const projectStatuses = useProjectStatuses();

  const [filters, setFilters] = useState(() => {
    const params = new URLSearchParams(location.search);
    return {
      name: params.get("name") || "",
      status: knownProjectStatuses(params.get("status")),
      skip: +(params.get("skip") || 0),
      limit: +(params.get("limit") || 10),
    };
  });

  useEffect(() => {
    if (noAccess) return;
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value || value === 0) params.set(key, `${value}`);
    });

    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
  }, [noAccess, filters, navigate, location.pathname]);

  const [searchName, setSearchName] = useState(filters.name);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleDebouncedSearchName = useCallback(
    debounce((name: string) => setFilters((filters) => ({ ...filters, name, skip: 0 })), 300),
    []
  );

  const hasActiveFilters = filters.name !== "" || filters.status !== "";
  const { currentData, isLoading, isFetching, isError, refetch } = useGetProjectsQuery(
    noAccess
      ? skipToken
      : {
          name: filters.name,
          status: filters.status,
          limit: filters.limit + 1,
          skip: filters.skip,
          withCount: hasActiveFilters || undefined,
        }
  );
  const { data: everyProject } = useGetProjectsQuery(
    noAccess || !hasActiveFilters ? skipToken : { limit: 1, skip: 0, withCount: true },
  );
  const projects = currentData?.projects.slice(0, filters.limit) ?? [];
  const hasNextPage = (currentData?.projects.length ?? 0) > filters.limit;

  const isEmptyList = !isFetching && currentData !== undefined && projects.length === 0 && filters.skip === 0;
  const resetFilters = () => {
    handleDebouncedSearchName.cancel();
    setSearchName("");
    setFilters((filters) => ({ ...filters, name: "", status: "", skip: 0 }));
  };

  const chosenStatuses = filters.status === "" ? [] : (filters.status.split(",") as ProjectStatus[]);
  const filterColumns: EntityTableColumn[] = [
    {
      key: "status",
      label: t("Status"),
      multiSelectConfig: {
        options: Object.values(ProjectStatus).map((status) => ({ value: status, label: projectStatuses[status].name })),
        selectedValues: chosenStatuses.map((status) => ({ value: status, label: projectStatuses[status].name })),
        onSelect: (values) =>
          setFilters((filters) => ({ ...filters, status: values.map((v) => v.value).join(","), skip: 0 })),
      },
    },
  ];
  const countText =
    currentData?.count != null && everyProject?.count != null
      ? t("{{shown}} of {{count}} projects", { shown: currentData.count, count: everyProject.count })
      : null;

  if (noAccess) {
    return (
      <>
        <Center py="20">
          <Text color="smMuted">{t("Your organization does not have access to this feature.")}</Text>
        </Center>
      </>
    );
  }

  return (
    <>
      <Grid
        h="100%"
        gridTemplateColumns="minmax(0, 1fr)"
        gridTemplateRows={"minmax(0, 1fr)"}
      >
        <GridItem h="100%" px="2" pb="6">
          <ListPageHeader
            heading={t("Projects")}
            {...listHeaderRow}
            mb="5"
            data-testid="list-header"
            actions={null}
          />
          <Box px="2">
            <FilterBar
              columns={filterColumns}
              onResetAll={resetFilters}
              countText={countText}
              search={{
                value: searchName,
                onChange: (name) => {
                  setSearchName(name);
                  handleDebouncedSearchName(name);
                },
                label: String(t("Search the table")),
                tooltip: String(t("Searches ID and Name")),
              }}
            />
            {!isLoading && isError && (
              <Box border="1px solid" borderColor="smBorder" rounded="md">
                <ListLoadError message={t("Could not load projects.")} onRetry={refetch} />
              </Box>
            )}
            {!isLoading && !isError && isEmptyList && (
              <Flex direction="column" align="center" gap="3" py="10" textAlign="center">
                <Text color="smMuted">
                  {hasActiveFilters ? t("No projects match your search or filters.") : t("No projects yet.")}
                </Text>
                {hasActiveFilters && (
                  <Button
                    leftIcon={<Icon as={FaUndo} />}
                    variant="outline"
                    colorScheme="gray"
                    color="smMuted"
                    size="sm"
                    onClick={resetFilters}
                  >
                    {t("Reset table")}
                  </Button>
                )}
              </Flex>
            )}
            {!isLoading && !isError && !isEmptyList && (
              <Flex flexDirection="column" border="1px solid" borderColor="smBorder" rounded="md" pt="2.5" pb="2.5">
                <Paging
                  skip={filters.skip}
                  limit={filters.limit}
                  setSkip={(skip: number) => setFilters((filters) => ({ ...filters, skip }))}
                  isLoading={isFetching}
                  hasNextPage={hasNextPage}
                />
                <Box width="full" maxWidth="full" overflowX="auto">
                  <Table size="sm">
                    <Thead>
                      <Tr>
                        <Th>{t("ID")}</Th>
                        <Th minWidth="80">{t("Name")}</Th>
                        <Th>{t("Status")}</Th>
                        <Th>{t("Type")}</Th>
                        <Th>{t("Start date")}</Th>
                        <Th>{t("End date")}</Th>
                        <Th>{t("Parent project")}</Th>
                        <Th>{t("Project responsible")}</Th>
                        <Th>{t("Sourcing responsible")}</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {projects.map((project) => (
                        <Tr
                          key={project.id}
                          onClick={() => goToProject(project.id)}
                          _hover={{ backgroundColor: "smBackgroundSecondary", cursor: "pointer" }}
                        >
                          <Td>{project.externalId}</Td>
                          <Td fontWeight="semibold">{project.name}</Td>
                          <Td>
                            <Tag colorScheme={projectStatuses[project.status].colorScheme}>
                              {projectStatuses[project.status].name}
                            </Tag>
                          </Td>
                          <Td>
                            {project.projectType && (
                              <Tag variant="subtle">
                                {projectTypes.find((t) => t.value === project.projectType?.id)?.label}
                              </Tag>
                            )}
                          </Td>
                          <Td>{project.startDate ? displayDate(project.startDate) : null}</Td>
                          <Td>{project.endDate ? displayDate(project.endDate) : null}</Td>
                          <Td>{`${project.parent?.externalId ? `${project.parent.externalId} - ` : ""}${
                            project.parent?.name ?? ""
                          }`}</Td>
                          <Td>{project.projectResponsible ? displayPersonName(project.projectResponsible) : null}</Td>
                          <Td>{project.sourcingResponsible ? displayPersonName(project.sourcingResponsible) : null}</Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                  {currentData && projects.length === 0 && filters.skip > 0 && (
                    <Text textAlign="center" color="smMuted" py="6">
                      {t("There are no more projects. Go back to the previous page.")}
                    </Text>
                  )}
                </Box>
                <Paging
                  skip={filters.skip}
                  limit={filters.limit}
                  setSkip={(skip: number) => setFilters((filters) => ({ ...filters, skip }))}
                  isLoading={isFetching}
                  hasNextPage={hasNextPage}
                />
              </Flex>
            )}
            {isLoading && <Box py="5">{skeletons.stackedLines(10, 5)}</Box>}
          </Box>
        </GridItem>
      </Grid>
    </>
  );
};
